// Hooks
import { useEffect, useState } from 'react';
import { useEditEntity } from '../../../../context/EditEntityContext';
import { useEditContactApi } from '../../../../context/EditContactApiContext';
// Api
// Utils
import { resolveUserLabel } from '../../utils/resolveUserLabel';
// Types
import { IBusiness } from '@trii/types/dist/Contacts';
import { GeneralProps } from '../General';
import { UserInfo } from '@trii/types/dist/Users';
// UI
import { Section, SectionContent } from '../../../../layout';
import BusinessInfoLabel from '../../../../shared/BusinessInfoLabel';
import { EntityCustomFieldLabel } from '../../../../shared';
import { useTranslationContext } from '../../../../context/TranslationContext';

export type BusinessGeneralProps = Omit<GeneralProps, 'entityData' | 'entityType'> & {
  entityType: 'business';
  entityData: IBusiness;
};

const BusinessGeneral = (props: BusinessGeneralProps) => {
  const t = useTranslationContext();

  const { entityData, customContactFields, baseUrl, spaceId, currentUser, onEntityUpdated } = props;

  const { entityType, editEntity } = useEditEntity();
  const api = useEditContactApi();

  if (entityType !== 'business') {
    return null;
  }

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [owner, setOwner] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);

  const editBusiness = editEntity;

  useEffect(() => {
    if (!api) return;
    const controller = new AbortController();

    api
      .fetchUsers({ signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setUsers(data ?? []);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setUsers([]);
      });

    return () => {
      controller.abort();
    };
  }, [api]);

  useEffect(() => {
    const controller = new AbortController();

    const getRemoteLabel = async (userId?: string) => {
      if (!userId) return null;
      if (!api) return null;
      try {
        const userTrii = await api.fetchUserTriiById({ userId, signal: controller.signal });
        return userTrii?.display_name || userTrii?.email || null;
      } catch {
        return null;
      }
    };

    const resolve = async () => {
      const ownerLabel =
        resolveUserLabel({ userId: entityData.owner, users, currentUser }) ??
        (entityData.owner ? await getRemoteLabel(entityData.owner) : null);

      const createdByLabel =
        resolveUserLabel({ userId: entityData.createdBy ?? undefined, users, currentUser }) ??
        (entityData.createdBy ? await getRemoteLabel(entityData.createdBy) : null);

      const updatedByLabel =
        resolveUserLabel({ userId: entityData.updatedBy ?? undefined, users, currentUser }) ??
        (entityData.updatedBy ? await getRemoteLabel(entityData.updatedBy) : null);

      if (controller.signal.aborted) return;

      setOwner(ownerLabel);
      setCreatedBy(createdByLabel);
      setUpdatedBy(updatedByLabel);
    };

    resolve();
    return () => {
      controller.abort();
    };
  }, [api, entityData, users, currentUser]);

  return (
    <Section title="General">
      <SectionContent>
        {/* BUSINESS LABELS */}
        <BusinessInfoLabel
          title={t('conversations.businessDetails.businessOwner')}
          value={entityData.owner}
          displayValue={owner || '-'}
          inputType="select"
          options={users}
          constructUpdateObject={(value) => ({
            owner: typeof value === 'string' ? value : '',
          })}
          baseUrl={baseUrl}
          spaceId={spaceId}
          businessId={entityData.id}
          onEntityUpdated={onEntityUpdated}
        />

        <BusinessInfoLabel
          title={t('conversations.contactDetails.createdBy')}
          value={createdBy || '-'}
          isNotEditable={true}
        />
        <BusinessInfoLabel
          title={t('conversations.contactDetails.modifiedBy')}
          value={updatedBy || '-'}
          isNotEditable={true}
        />

        {editBusiness.field.customProperties.map((property) => (
          <EntityCustomFieldLabel
            key={property.nameKey}
            property={property}
            entity={editBusiness}
            entityType="business"
            entityId={String(entityData.id)}
            customContactFields={customContactFields}
            userTrii={currentUser}
            baseUrl={baseUrl}
            spaceId={spaceId}
            onEntityUpdated={onEntityUpdated}
          />
        ))}
        {/*END OF BUSINESS LABELS */}
      </SectionContent>
    </Section>
  );
};

export default BusinessGeneral;
